# Don't combine with a PackageSearch on the same page.
# Use either or.
// Migrated
<template>
  <div
    id="destination-dealfinder"
    class="w-12/12"
  >
    <client-only>
      <template v-if="hydrated">
        <LazyCalendarSearchBar
          ref="csb"
          :class="{'mb-12': $isSol}"
          @duration="selectDuration"
          @update="refetch"
          @initialized="searchBarInitialized"
          @on-set-active-search="handleSearchBarDropdownToggle"
        />
        <LazyAlert
          v-if="refetchedMoreStopovers && !refetching && !showNoneFound"
          type="info"
          data-i18n="dealfinderRetriedStopovers"
        >
          {{ $t('dealfinderRetriedStopovers') }}
        </LazyAlert>
        <div
          :key="calendarKey + 1"
          class="charter-destination__deal-finder package-search"
          :class="{ 'pointer-events-none opacity-50': isCalendarDisabled }"
        >
          <LazyDealFinderMonthSlider
            :loading="loading || !isFullyLoaded"
            :error="showNoneFound && !refetching"
            is-deal-finder
            @go-to-month="goToMonth"
          />
          <LazyDealFinderCalendar
            :error="showNoneFound && !refetching"
            :loading="loading || !isFullyLoaded"
            @select="selectPackage"
          >
            <template #error>
              <p class="text-center text-black">
                {{ $t('charterDealFinderNoResults') }}
              </p>
              <LazyDealFinderHotelIsAdultsOnly />
            </template>
            <template #footer>
              <div
                class="flex items-center mt-4"
                :class="$isSol ? 'justify-end' : 'justify-center'"
              >
                <span
                  v-if="!showNoneFound"
                  class="price-per-person ml-1 text-left md:text-center mr-2"
                >
                  <strong>
                    {{ $t('charterDealfinderPricePerPerson') }}
                  </strong>
                </span>
                <span
                  v-if="$isSol"
                  class="open-purchase flex flex-row ml-1"
                >
                  <div class="flex open-purchase-ball" />
                  <div class="flex open-purchase-text ml-2">
                    {{ '= ' + $t('charterLegendRecommendedDates') }}
                  </div>
                </span>
                <span
                  v-if="$isSol"
                  class="best-price flex flex-row ml-1"
                >
                  <div class="flex best-price-ball" />
                  <div class="flex best-price-text ml-2">
                    {{ '= ' + $t('bestPrice') }}
                  </div>
                </span>
              </div>
            </template>
          </LazyDealFinderCalendar>
          <div
            v-if="refetching || isExiting"
            class="loading-overlay"
          >
            <LazyLoading v-if="!isExiting" />
          </div>
        </div>
      </template>
    </client-only>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'

import events from '@layers/web/constants/events.js'

export default defineNuxtComponent({
  setup () {
    const { urls: localeURLs } = useLocale()

    return {
      localeURLs,
    }
  },

  props: {
    displayTitle: {
      type: Boolean,
      default: true,
    },

    emitSelection: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['scrollToDealfinder', 'select', events.dealfinderUpdateCalendarDate],

  data () {
    return {
      refetching: true,

      calendarKey: 10000,

      selectedDuration: {},

      initialized: false,
      searchBarIsInitialized: false,
      hydrated: false,
      isExiting: false,

      timer: null,

      isCalendarDisabled: false,
    }
  },

  computed: {
    ...mapState(useCharterPackagesStore, {
      loading: 'loading',
    }),

    ...mapState(useDealfinderStore, {
      destination: 'destination',
      dates: 'dealDates',
      refetchedMoreStopovers: 'refetchedMoreStopovers',
    }),

    showNoneFound () {
      return !this.loading && (this.dates || []).length === 0
    },

    isFullyLoaded () {
      return this.initialized && this.searchBarIsInitialized
    },
  },

  watch: {
    selectedDuration () {
      this.calendarKey += 1
    },

    '$route.query.showRooms' () {
      this.isExiting = !!this.$route.query?.showRooms
    },
  },

  mounted () {
    if (this.$route.query.df !== 'false') {
      this.$emit('scrollToDealfinder')
    }

    this.hydrated = true
    this.$event.$on(events.dealfinderLoading, this.setIsFetching)
  },

  beforeMount () {
    this.initializeState()
  },

  beforeUnmount () {
    clearTimeout(this.timer)
    this.$event.$off(events.dealfinderLoading, this.setIsFetching)
  },

  methods: {
    ...mapActions(useDealfinderStore, {
      refetchState: 'repopulate',
      initializeState: 'init',
      prepareSearch: 'prepareSearch',
    }),

    setIsFetching (v) {
      // catch all falsy
      this.refetching = !!v
      if (!v && !this.initialized) {
        this.initialized = true
      }
    },

    // Check
    async selectPackage (day) {
      this.isExiting = true
      if (this.emitSelection) {
        this.$emit('select', day)
      } else {
        const options = await this.prepareSearch(day)

        // NOTE hack so that adjacent dates requests are not blocked (see pages/packagessearch.vue asyncData hook), potential refactor is to replace the global loading state by local loading states
        this.$router.push(`${this.localeURLs.packagessearch}?${options}`).catch(() => { })
      }
      return {}
    },

    selectDuration () {
      this.refetching = false
      this.calendarKey += 1
      this.$nextTick(() => { this.calendarKey += 1 })
    },

    // Check
    refetch () {
      this.refetchState()
      this.calendarKey += 1
      this.refetching = false
    },

    // Check
    goToMonth (val) {
      this.$event.$emit(events.dealfinderUpdateCalendarDate, val)
    },

    searchBarInitialized (val) {
      this.searchBarIsInitialized = val
    },

    handleSearchBarDropdownToggle (search) {
      this.isCalendarDisabled = !!search
    },
  },
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/adjacent-dates-carousel/_slider";
@import "@layers/web/assets/scss/modules/adjacent-dates-carousel/_slides";

.__dealfinder-overlay {
  position: absolute;
  top: -1rem;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffffcc;
  z-index: 100;
  display: flex;
  flex: 1 1 0%;
  justify-content: center;
  align-items: center;
}
.loading-overlay {
  @extend .__dealfinder-overlay;
}
.charter-destination {
  &__deal-finder {
    @media (min-width:theme('screens.md')) {
      margin: 0 -45px;
    }
    @media (min-width:theme('screens.md')) {
      margin: 0 -30px;
      .charter-results {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
      }
      &-calendar {
        padding: 0 !important;

        .vfc-main-container {
          .vfc-week {
            .vfc-day {
              &.vfc-week-number {
                width: 5%;
                min-width: 1px;
                max-width: 20px !important;
              }
              // margin: 0;
              width: calc(95% / 7) !important;

              .dealfinder-date {
                font-size:.8rem;
              }
            }
          }
        }
      }
    }
    @media (max-width:theme('screens.sm')) {
      .charter-results {
        padding-left: 1.25rem;
        padding-right: 1.25rem;
      }
      &-calendar {
        padding: 0 !important;

        .vfc-main-container {
          .vfc-week {
            .vfc-day {
              // margin: 0;
              width: calc(100% / 7) !important;

              .dealfinder-date {
                font-size:.6rem;
              }
            }
          }
        }
      }
    }
    @media (min-width:460px) and (max-width: calc(theme('screens.md') - 1px)) {
      &-calendar .vfc-main-container .vfc-week .vfc-day .dealfinder-date{
        font-size: .8rem !important;
      }
    }
    &-dropdown > div {
      border-radius: 10px;
    }

    &-calendar-overlay {
      @extend .__dealfinder-overlay;
      top: 0;
      z-index: 101;
    }

    .adjacent-dates-carousel {
      & > .swiper-container {
        // To remove date border
        clip-path: inset(0 0 0 1px);
      }
    }

    @media (max-width: theme('screens.lg')) {
      .adjacent-dates-carousel-slide {
        margin-top: .6rem;
      }
    }
  }
}
#destination-dealfinder {
  min-height: 300px;
  &.--overlay{
    .charter-destination__deal-finder-calendar {
      -webkit-filter: grayscale(100%);
      filter: grayscale(100%);
      &:before {
        content:"\a0";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: block;
        background:rgba(255,255,255,0.3);
        // date buttons on #100
        z-index: 101;
      }
    }
  }

  .price-per-person {
    font-size: 0.8rem;
  }

  .open-purchase {

    .open-purchase-ball {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: theme('colors.blue-special');
    }
    .open-purchase-text {
      font-size: 0.8rem;
      line-height: 20px;
      font-weight: bold;
    }
  }

  .best-price {

    .best-price-ball {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: theme('colors.green-special');
    }
    .best-price-text {
      font-size: 0.8rem;
      line-height: 20px;
      font-weight: bold;
    }
  }
}
.dealfinder-date.highlighted.blink .text-red {
  color: theme('colors.orange.DEFAULT') !important;
}
.identity--rolfs {
  @media (max-width: theme('screens.md')) {
    .charter-results {
      padding: 0;
      margin: 0 -20px;
    }
  }
}
</style>
